import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Crown, Sparkles, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { logPaywall } from '../lib/paywallAnalytics';
import { getUpgradeVariant, getUpgradeCopy } from '../lib/upgradeExperiment';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * "Upgrade to unlock" paywall for signed-in FREE users who tap a Premium
 * feature (Strong's, commentaries, dictionaries, maps, etc.).
 *
 * If the user hasn't used their free trial yet, the primary CTA starts the
 * 15-day trial in place (no card) — turning the locked-feature moment into
 * instant full access, the single highest-converting trial trigger. Otherwise
 * it routes to the pricing page. Copy is A/B tested via upgradeExperiment.
 */
const UpgradePromptDialog = ({ open, feature, onClose }) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user, refreshUser } = useAuth();
  const es = language === 'es';
  const variant = getUpgradeVariant();
  const copy = getUpgradeCopy(variant, feature, es);
  const [starting, setStarting] = useState(false);

  const trialAvailable = !!user && !user.trial_used;

  const goToPricing = () => { logPaywall('clicked', feature, 'free'); navigate('/pricing'); };

  const startTrial = async () => {
    setStarting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/payments/start-trial`, { source: 'paywall' }, { withCredentials: true });
      logPaywall('clicked', `${feature || 'feature'} (trial-start)`, 'free');
      await refreshUser();
      toast.success(
        es
          ? '¡Prueba de 15 días activada! Toca de nuevo para desbloquear.'
          : '15-day trial started! Tap the feature again to unlock it.',
      );
      onClose();
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.info(es ? 'Ya usaste tu prueba. Mejora para continuar.' : "You've already used your trial — upgrade to continue.");
        goToPricing();
      } else {
        toast.error(es ? 'No se pudo iniciar la prueba.' : "Couldn't start the trial. Please try again.");
      }
    } finally {
      setStarting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { logPaywall('dismissed', feature, 'free'); onClose(); } }}>
      <DialogContent className="sm:max-w-md" data-testid="upgrade-prompt-dialog" data-variant={variant} data-trial-available={trialAvailable ? '1' : '0'}>
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-amber-600 shadow-lg shadow-amber-500/30">
            <Crown className="h-6 w-6 text-white" />
          </div>
          <DialogTitle className="text-center text-xl" data-testid="upgrade-prompt-title">
            {trialAvailable
              ? (es ? 'Desbloquéalo gratis por 15 días' : 'Unlock it free for 15 days')
              : copy.title}
          </DialogTitle>
          <DialogDescription className="text-center" data-testid="upgrade-prompt-desc">
            {trialAvailable
              ? (es
                  ? `Inicia tu prueba Premium — acceso completo a ${feature || 'todas las herramientas'}, sin tarjeta.`
                  : `Start your Premium trial — full access to ${feature || 'every study tool'}, no card required.`)
              : copy.description}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col sm:flex-col gap-2">
          {trialAvailable ? (
            <>
              <Button
                onClick={startTrial}
                disabled={starting}
                className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white"
                data-testid="upgrade-prompt-trial-cta"
              >
                {starting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                {es ? 'Iniciar prueba gratis de 15 días' : 'Start 15-day free trial'}
              </Button>
              <Button
                variant="ghost"
                onClick={goToPricing}
                className="w-full"
                data-testid="upgrade-prompt-cta"
              >
                {es ? 'Ver planes' : 'See plans'}
              </Button>
            </>
          ) : (
            <Button
              onClick={goToPricing}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white"
              data-testid="upgrade-prompt-cta"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              {copy.cta}
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} className="w-full" data-testid="upgrade-prompt-dismiss">
            {es ? 'Ahora no' : 'Not now'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradePromptDialog;
